/**
 * FilterBar - renders main-placement filter plugins.
 *
 * Provides: Clear button, Advanced filters button (with badge),
 * Save as alert button, and ARIA live region for totalCount.
 *
 * ARIA: role="group" aria-label="Filtry naborow" aria-busy={isLoading}
 * Live region: role="status" aria-live="polite" with totalCount (WCAG 4.1.3)
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 3.4)
 */

import React, { useCallback, useRef } from "react";
import { FilterPlugin } from "./types";
import { getMainFilters } from "./registry";
import { AlertFilters } from "../../../../types/grants";

export interface FilterBarProps {
  /** The filter plugin registry */
  registry: ReadonlyArray<FilterPlugin<unknown>>;
  /** Current filter values keyed by plugin key */
  filterValues: Record<string, unknown>;
  /** Callback when a filter value changes */
  onFilterChange: (key: string, value: unknown) => void;
  /** Callback to clear all filters */
  onClearAll: () => void;
  /** Callback to open advanced filters modal */
  onAdvancedFiltersClick: () => void;
  /** Number of active advanced filter categories */
  advancedFiltersCount: number;
  /** Whether any filter has a non-default value */
  hasActiveFilters: boolean;
  /** True during initial data load (shows skeleton) */
  isLoading?: boolean;
  /** True during refetch after filter change */
  isFetching?: boolean;
  /** Total count of results for ARIA live region (WCAG 4.1.3) */
  totalCount?: number;
  /** Faceted search counts from backend (per ADR-027) */
  facets?: Record<string, unknown>;
  /** Callback when user clicks "Save as alert" */
  onSaveAsAlert?: (
    filters: AlertFilters,
    triggerRef: React.RefObject<HTMLButtonElement | null>
  ) => void;
  /** Whether the user has reached the maximum number of alerts */
  alertLimitReached?: boolean;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  registry,
  filterValues,
  onFilterChange,
  onClearAll,
  onAdvancedFiltersClick,
  advancedFiltersCount,
  hasActiveFilters,
  isLoading = false,
  isFetching = false,
  totalCount,
  facets,
  onSaveAsAlert,
  alertLimitReached = false,
}) => {
  const saveAsAlertButtonRef = useRef<HTMLButtonElement>(null);
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;
  const pluginHandlersRef = useRef<Record<string, (value: unknown) => void>>({});
  const mainPlugins = getMainFilters(registry);

  const getPluginChangeHandler = useCallback(
    (key: string) => {
      if (!pluginHandlersRef.current[key]) {
        pluginHandlersRef.current[key] = (newValue: unknown) =>
          onFilterChangeRef.current(key, newValue);
      }
      return pluginHandlersRef.current[key];
    },
    []
  );


  return (
    <div
      className="sticky top-0 bg-white z-10 pb-4 border-b border-neutral-200"
      role="group"
      aria-label="Filtry naborów"
      aria-busy={isLoading || isFetching}
    >
      {/* ARIA live region for screen readers (WCAG 4.1.3 Status Messages) */}
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {totalCount !== undefined && `${totalCount}`}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {/* Main filter plugins - each plugin renders its own container */}
        {mainPlugins.map((plugin) => {
          // Hidden plugins (Component: null) are filtered out by getMainFilters,
          // but guard here for type safety.
          if (!plugin.Component) return null;
          const PluginComponent = plugin.Component;
          const value = filterValues[plugin.key] ?? plugin.defaultValue;
          return (
            <PluginComponent
              key={plugin.key}
              value={value}
              onChange={getPluginChangeHandler(plugin.key)}
              facets={facets}
              isLoading={isLoading}
              isFetching={isFetching}
            />
          );
        })}

        {/* Clear filters button */}
        {hasActiveFilters && (
          <button
            onClick={onClearAll}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-neutral-600 bg-neutral-100 rounded-md hover:bg-neutral-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Wyczyść wszystkie filtry"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-4 h-4 mr-1"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            Wyczyść
          </button>
        )}

        {/* Advanced filters button */}
        <button
          onClick={onAdvancedFiltersClick}
          data-testid="advanced-filters-button"
          data-tour="calls-advanced-filters"
          className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            advancedFiltersCount > 0
              ? "text-primary-700 bg-primary-100 border border-primary-300"
              : "text-primary-600 bg-primary-50 hover:bg-primary-100"
          }`}
          aria-label={
            advancedFiltersCount > 0
              ? `Więcej filtrów (${advancedFiltersCount} aktywnych)`
              : "Więcej filtrów"
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-4 h-4 mr-1"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"
            />
          </svg>
          Więcej
          {advancedFiltersCount > 0 && (
            <span
              aria-hidden="true"
              data-testid="advanced-filters-badge"
              className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-primary-600 rounded-full"
            >
              {advancedFiltersCount}
            </span>
          )}
        </button>

        {/* Save as alert button */}
        {onSaveAsAlert && (
          <button
            ref={saveAsAlertButtonRef}
            onClick={() => {
              // Alert saving logic delegated to parent via callback
              onSaveAsAlert({} as AlertFilters, saveAsAlertButtonRef);
            }}
            disabled={alertLimitReached}
            data-tour="calls-save-alert"
            className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              !alertLimitReached
                ? "text-neutral-700 bg-white border border-neutral-300 hover:bg-neutral-50"
                : "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed"
            }`}
            aria-label={
              alertLimitReached
                ? "Osiągnięto maksymalną liczbę alertów"
                : "Zapisz bieżące wyszukiwanie jako alert"
            }
            aria-haspopup="dialog"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4 md:mr-1"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
              />
            </svg>
            <span className="hidden md:inline">Zapisz jako alert</span>
          </button>
        )}
      </div>
    </div>
  );
};
